// Controlled inline composer for an optional note inside a wizard step.
// The wizard parent owns the DraftNote state so it survives step navigation
// (Previous / step-indicator Edit) and is available to flushNoteStepDraft
// after entity create/update succeeds.
import { useTranslation } from "react-i18next";

import { useAppContext } from "@context/AppContext";
import Heading from "@components/layout/Heading";
import Label from "@components/form/Label";
import TextArea from "@components/form/TextArea";
import CheckboxInputWithLabel from "@components/form/CheckboxInputWithLabel";

export type DraftNote = {
  body: string;
  isReportable: boolean;
  // Only consumed by the audit-trail-panel composer (post-creation).
  // Wizard-inline NoteComposer never sets this — the evidence dropzone in
  // the same step is the canonical upload path.
  attachment?: File;
};

// Matches the backend cap in AdminCreateNoteValidator (maxLength: 8000).
export const NOTE_BODY_MAX_LENGTH = 8000;

export const EMPTY_DRAFT_NOTE: DraftNote = {
  body: "",
  isReportable: false,
};

export const isEmptyDraftNote = (d: DraftNote | null | undefined) =>
  !d || !d.body.trim();

type NoteComposerProps = {
  value: DraftNote;
  onChange: (next: DraftNote) => void;
  idPrefix?: string;
};

const NoteComposer: React.FunctionComponent<NoteComposerProps> = ({
  value,
  onChange,
  idPrefix = "note-composer",
}) => {
  const { t } = useTranslation();
  const { checkPermissions } = useAppContext();

  if (!checkPermissions(["CreateNotes"])) {
    return null;
  }

  return (
    <section className="space-y-4">
      <div className="space-y-1">
        <Heading light>{t("notes.composer_section_title")}</Heading>
        <p className="text-sm text-gray-500">
          {t("notes.composer_section_helper")}
        </p>
      </div>

      <div>
        <Label htmlFor={`${idPrefix}-body`}>{t("notes.body_label")}</Label>
        <TextArea
          id={`${idPrefix}-body`}
          value={value.body}
          onChange={e => onChange({ ...value, body: e.target.value })}
          rows={4}
          maxLength={NOTE_BODY_MAX_LENGTH}
          placeholder={t("notes.composer_placeholder") as string}
        />
      </div>

      <CheckboxInputWithLabel
        htmlFor={`${idPrefix}-reportable`}
        checked={value.isReportable}
        onChange={e => onChange({ ...value, isReportable: e.target.checked })}
        label={t("notes.reportable_label")}
        labelDescription={t("notes.reportable_badge_tooltip") as string}
        inline
      />
    </section>
  );
};

export default NoteComposer;
